<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useWikiPagesStore } from '../stores/wiki_pages'

// Popover content for an inline citation marker. Looks up the page in
// the wiki_pages catalog by path, lazily fetches the snippet via the
// store's `fetchSnippet`, and renders title + snippet + open links.
//
// Falls back gracefully when the cited path isn't in the catalog
// (deleted page, stale citation): shows the path as the title and
// drops the snippet + open-page button — only the path text remains
// so the user can at least see what was cited.
const props = defineProps<{
  // Lower-cased on-disk wiki path captured from the marker's
  // data-cite-path attribute (e.g. "entities/anthropic.md").
  path: string
  // 1-indexed citation number from the marker's data-cite-index, used
  // for the visible "[N]" prefix on the title row.
  index: number
}>()

const wikiPages = useWikiPagesStore()
const { pages } = storeToRefs(wikiPages)

const page = computed(() => pages.value.find((p) => p.path === props.path) ?? null)

// Display title for the title row. When the cited page is missing
// from the catalog (deleted, renamed) the path itself becomes the
// stand-in label so the user sees what was cited rather than a blank.
const displayTitle = computed(() => page.value?.title || props.path)

// Snippet is loaded lazily — `null` while pending, `''` if the page
// has no extractable prose (e.g. all headings), otherwise the
// first-paragraph excerpt. Track loading separately so the template
// can branch between "loading shimmer" and "no preview available".
const snippet = ref<string | null>(null)
const loading = ref(false)

// Re-fetch when the path changes (the manager reuses one card across
// hovers on different markers). Skipped when the page isn't in the
// catalog — no id to fetch by.
watch(
  () => page.value?.id ?? null,
  async (id) => {
    if (id == null) {
      snippet.value = null
      loading.value = false
      return
    }
    loading.value = true
    snippet.value = null
    try {
      snippet.value = await wikiPages.fetchSnippet(id)
    } finally {
      loading.value = false
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="flex max-w-xs flex-col gap-2 text-ink" data-testid="citation-hover-card">
    <header class="flex min-w-0 items-center gap-2">
      <span
        class="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md bg-brand-primary/15 px-1.5 text-xs font-semibold text-brand-primary"
        data-testid="citation-index"
        >{{ index }}</span
      >
      <h4 class="m-0 truncate text-sm font-semibold">{{ displayTitle }}</h4>
    </header>

    <p v-if="page" class="m-0 truncate font-mono text-xs text-ink-faint">{{ page.path }}</p>

    <p v-if="loading" class="m-0 text-xs italic text-ink-faint" data-testid="citation-snippet-loading">
      Loading preview…
    </p>
    <p
      v-else-if="snippet"
      class="m-0 line-clamp-3 font-serif text-[15px] leading-snug text-ink-reading"
      data-testid="citation-snippet"
    >
      {{ snippet }}
    </p>
    <p v-else-if="page" class="m-0 text-xs italic text-ink-faint">No preview available.</p>
    <p v-else class="m-0 text-xs italic text-ink-faint" data-testid="citation-missing">
      This page is no longer in the brain.
    </p>

    <footer v-if="page" class="flex items-center gap-3 border-t border-border-divider pt-2">
      <RouterLink
        :to="`/p/${page.path}`"
        class="inline-flex min-h-6 items-center text-xs text-brand-primary no-underline hover:underline"
        data-testid="citation-open"
        >Open page →</RouterLink
      >
    </footer>
  </div>
</template>
